// navigation/rail/nav-rail.tsx
//
// The Upmode navigation rail, direction 2A "labeled plinth" (UPM-140).
// Recreated from the approved CD handoff in the app's own stack: React + TS +
// lucide-react + the repo's theme tokens. The prototype's template runtime,
// inline styles and SVG sprite are deliberately not ported.
//
// Three rulings from the brief are load-bearing:
//   1. Advanced is bottom-anchored, collapsed by default, and expands UPWARD.
//      It never displaces the core five.
//   2. Advanced modules are locked for client seats and show a padlock. The
//      locked row is a quiet upsell surface, not an error.
//   3. Module names and the order of the core five are locked.
//
// One structure for both populations: entitlement is a per-row STATE, not a
// different layout. Client seats and admins see the same twelve rows.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Search, ChevronDown, ChevronUp, ChevronRight, ChevronsLeft, ChevronsRight,
  Lock, Settings, Sun, Moon, Plus, Check, LogOut,
} from "lucide-react";
import { useTheme } from "@/app/contexts/theme-context";
import {
  ADVANCED_IDS, FEATURES, MANAGE_ITEMS, RAIL_GROUPS, featureForPage,
  type FeatureId, type NavFeature,
} from "../nav-config";
import { useEntitlements, stateOf, type FeatureStates } from "../use-entitlements";
import { LockedFeatureModal } from "../locked-feature-modal";
import wordmarkPrimary from "@/app/assets/brand/upmode-wordmark-primary.png";
import wordmarkInverse from "@/app/assets/brand/upmode-wordmark-inverse.png";
import iconPrimary from "@/app/assets/brand/upmode-icon-primary.png";
import iconInverse from "@/app/assets/brand/upmode-icon-inverse.png";
import "./nav-rail.css";

const ADV_OPEN_KEY = "up_pref_nav_advanced_open";

export type RailState = "ready" | "loading" | "empty";

export interface NavRailProps {
  /** Route id. activeModuleId derives from this, never from local state. */
  currentPage: string;
  onNavigate: (page: string) => void;
  onLogout: () => void;
  /** Collapsed is OWNED BY THE SHELL so the main column animates with it. */
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  activeBrandName?: string;
  /** Opens the existing command palette. No palette is invented here. */
  onOpenCommandPalette?: () => void;
  onOpenBrandSwitcher?: () => void;
  /** Open-actions count for the Action Items row, from the existing query. */
  openActionsCount?: number;
  /** "empty" when the seat has no analyses yet. "loading" is derived from the
   *  entitlement fetch below unless the shell forces it. */
  railState?: RailState;
}

function initialsOf(name?: string): string {
  const w = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!w.length) return "U";
  if (w.length === 1) return w[0].slice(0, 2).toUpperCase();
  return (w[0][0] + w[1][0]).toUpperCase();
}

function initialOf(name?: string): string {
  const c = (name || "").trim()[0];
  return c ? c.toUpperCase() : "U";
}

function featuresByIds(ids: FeatureId[]): NavFeature[] {
  return ids
    .map((id) => FEATURES.find((f) => f.id === id))
    .filter((f): f is NavFeature => !!f);
}

export function NavRail(props: NavRailProps) {
  const {
    currentPage, onNavigate, onLogout, collapsed, onCollapsedChange,
    activeBrandName, onOpenCommandPalette, onOpenBrandSwitcher,
    openActionsCount, railState,
  } = props;

  const { theme, setTheme } = useTheme();
  const { states, user, loading } = useEntitlements();

  const [advancedOpen, setAdvancedOpen] = useState<boolean>(() => {
    try { return localStorage.getItem(ADV_OPEN_KEY) === "true"; } catch { return false; }
  });
  const [manageOpen, setManageOpen] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [lockedModal, setLockedModal] = useState<FeatureId | null>(null);
  const [tip, setTip] = useState<{ text: string; top: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // Collapsed by default, persisted per user.
  useEffect(() => {
    try { localStorage.setItem(ADV_OPEN_KEY, String(advancedOpen)); } catch { /* ignore */ }
  }, [advancedOpen]);

  const displayName = user?.full_name?.trim() || user?.username?.split("@")[0] || "";
  const isAdmin = !!user?.is_admin;
  const isAgent = user?.user_type === "agent" || user?.user_type === "admin" || isAdmin;
  const roleLabel = isAdmin ? "Administrator" : isAgent ? "Agent user" : "Member";

  const coreGroups = useMemo(
    () => RAIL_GROUPS.map((g) => ({ label: g.label, items: featuresByIds(g.ids) })),
    [],
  );
  const advancedItems = useMemo(() => featuresByIds(ADVANCED_IDS), []);
  const visibleAdvanced = useMemo(
    () => advancedItems.filter((f) => stateOf(states, f.id) !== "hidden"),
    [advancedItems, states],
  );
  const lockedCount = useMemo(
    () => visibleAdvanced.filter((f) => stateOf(states, f.id) === "locked").length,
    [visibleAdvanced, states],
  );

  const manageItems = MANAGE_ITEMS.filter(
    (m) => (!m.adminOnly || isAdmin) && (!m.agentOnly || isAgent),
  );

  const state: RailState = railState ?? (loading ? "loading" : "ready");
  const activeFeature = featureForPage(currentPage);
  const activeModuleId = activeFeature?.id ?? null;

  const closeMenus = useCallback(() => {
    setManageOpen(false);
    setSwitcherOpen(false);
  }, []);

  const go = useCallback((page: string) => {
    onNavigate(page);
    closeMenus();
  }, [onNavigate, closeMenus]);

  // The list arrow keys walk: the core five, plus Advanced modules when they
  // are live AND the drawer is open.
  const walkable = useMemo(() => {
    const core = coreGroups.flatMap((g) => g.items);
    const adv = advancedOpen
      ? visibleAdvanced.filter((f) => stateOf(states, f.id) === "live")
      : [];
    return [...core, ...adv];
  }, [coreGroups, advancedOpen, visibleAdvanced, states]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const tag = (document.activeElement?.tagName || "").toUpperCase();
    if (tag === "INPUT" || tag === "TEXTAREA") return;

    if (e.key === "Escape") { closeMenus(); return; }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!walkable.length) return;
      const i = walkable.findIndex((f) => f.id === activeModuleId);
      const next = e.key === "ArrowDown"
        ? walkable[(i + 1 + walkable.length) % walkable.length]
        : walkable[(i - 1 + walkable.length) % walkable.length];
      if (next) go(next.page);
      return;
    }
    const k = e.key.toLowerCase();
    if (k === "a") { e.preventDefault(); setAdvancedOpen((v) => !v); return; }
    if (k === "m") { e.preventDefault(); setManageOpen((v) => !v); setSwitcherOpen(false); return; }
    if (k === "n") { e.preventDefault(); setSwitcherOpen((v) => !v); setManageOpen(false); return; }
    if (k === "t") { e.preventDefault(); setTheme(theme === "dark" ? "light" : "dark"); return; }
    if (e.key === "[") { e.preventDefault(); onCollapsedChange(true); return; }
    if (e.key === "]") { e.preventDefault(); onCollapsedChange(false); return; }
    if (e.key === "/") { e.preventDefault(); onOpenCommandPalette?.(); return; }
  };

  const wordmark = theme === "dark" ? wordmarkInverse : wordmarkPrimary;
  const iconmark = theme === "dark" ? iconInverse : iconPrimary;

  // ── Row ─────────────────────────────────────────────────────────────────
  const renderRow = (f: NavFeature, opts?: { advanced?: boolean }) => {
    const st = stateOf(states, f.id);
    if (st === "hidden") return null;
    const locked = st === "locked";
    const active = activeModuleId === f.id;
    const Icon = f.icon;
    const count = f.id === "action-items" ? openActionsCount : undefined;

    return (
      <button
        key={f.id}
        type="button"
        className={[
          "nr-row",
          opts?.advanced ? "nr-adv-row" : "",
          active ? "nr-row--active" : "",
          locked ? "nr-row--locked" : "",
        ].filter(Boolean).join(" ")}
        aria-current={active ? "page" : undefined}
        aria-disabled={locked || undefined}
        onClick={(e) => {
          // Locked click is a no-op: the row is an upsell surface, not an error.
          if (locked) { e.preventDefault(); setLockedModal(f.id); return; }
          go(f.page);
        }}
      >
        <span className="nr-row-icon"><Icon size={opts?.advanced ? 16 : 17} strokeWidth={1.75} /></span>
        <span className="nr-row-label">{f.label}</span>
        {count != null && count > 0 && !locked && <span className="nr-row-count">{count}</span>}
        {locked && <span className="nr-lock"><Lock size={12} strokeWidth={1.75} /></span>}
      </button>
    );
  };

  const renderChip = (f: NavFeature, i: number) => {
    const st = stateOf(states, f.id);
    if (st === "hidden") return null;
    const locked = st === "locked";
    const active = activeModuleId === f.id;
    const Icon = f.icon;
    return (
      <button
        key={f.id}
        type="button"
        aria-label={f.label}
        aria-current={active ? "page" : undefined}
        className={["nr-chip", active ? "nr-chip--active" : "", locked ? "nr-chip--locked" : ""].filter(Boolean).join(" ")}
        onClick={(e) => {
          if (locked) { e.preventDefault(); setLockedModal(f.id); return; }
          go(f.page);
        }}
        onMouseEnter={(e) => setTip({ text: f.label, top: e.currentTarget.getBoundingClientRect().top + 18 })}
        onMouseLeave={() => setTip(null)}
        onFocus={(e) => setTip({ text: f.label, top: e.currentTarget.getBoundingClientRect().top + 18 })}
        onBlur={() => setTip(null)}
        style={{ animationDelay: `${i * 22}ms` }}
      >
        <Icon size={17} strokeWidth={1.75} />
      </button>
    );
  };

  // ── Loading skeleton ────────────────────────────────────────────────────
  if (state === "loading") {
    const widths = [132, 96, 112, 150, 104];
    return (
      <div className={`nr-root${collapsed ? " nr-root--collapsed" : ""}`} aria-busy="true">
        <div className="nr-mark" style={{ opacity: 0.35 }}>
          <img src={collapsed ? iconmark : wordmark} alt="Upmode" />
        </div>
        {!collapsed && (
          <>
            <div className="nr-switch-wrap"><div className="nr-sk-block" style={{ height: 42 }} /></div>
            <div className="nr-search-wrap"><div className="nr-sk-outline" /></div>
          </>
        )}
        <div className="nr-nav">
          <div className="nr-group" style={{ gap: 14, paddingTop: 8 }}>
            {widths.map((w, i) => (
              <div key={i} className="nr-sk-bar" style={{ width: collapsed ? 36 : w, animationDelay: `${i * 90}ms` }} />
            ))}
          </div>
        </div>
        <div className="nr-plinth">
          <div style={{ padding: 12 }}><div className="nr-sk-bar" style={{ width: collapsed ? 36 : 88 }} /></div>
        </div>
      </div>
    );
  }

  const empty = state === "empty";

  return (
    <>
      <div
        ref={rootRef}
        className={`nr-root${collapsed ? " nr-root--collapsed" : ""}`}
        tabIndex={0}
        onKeyDown={onKeyDown}
        role="navigation"
        aria-label="Primary"
      >
        {/* 1. Wordmark. Swaps to the inverse mark in dark: the primary mark is
            black on transparent and would vanish on the dark surface. */}
        <div className="nr-mark">
          <img src={collapsed ? iconmark : wordmark} alt="Upmode" />
        </div>

        {/* 2. Client switcher */}
        {!collapsed && !empty && (
          <div className="nr-switch-wrap">
            <button
              type="button"
              className="nr-switch"
              aria-expanded={switcherOpen}
              onClick={() => { setSwitcherOpen((v) => !v); setManageOpen(false); }}
            >
              <span className="nr-avatar">{initialOf(activeBrandName)}</span>
              <span className="nr-switch-name">{activeBrandName || "No analysis"}</span>
              <span className="nr-live-dot" />
              <span className={`nr-chev${switcherOpen ? " nr-chev--open" : ""}`}>
                <ChevronDown size={14} strokeWidth={1.75} />
              </span>
            </button>
            {switcherOpen && (
              <>
                <div className="nr-backdrop" onClick={closeMenus} />
                <div className="nr-menu nr-menu--switcher" role="menu">
                  <button type="button" className="nr-menu-row" role="menuitem" onClick={() => { closeMenus(); onOpenBrandSwitcher?.(); }}>
                    <span className="nr-menu-row-icon"><Check size={15} strokeWidth={1.75} /></span>
                    {activeBrandName || "No analysis"}
                  </button>
                  <button type="button" className="nr-menu-row" role="menuitem" onClick={() => go("brand-search")}>
                    <span className="nr-menu-row-icon"><Plus size={15} strokeWidth={1.75} /></span>
                    New analysis
                    <kbd className="nr-kbd nr-kbd--row">N</kbd>
                  </button>
                  <div className="nr-menu-sep" />
                  {/* Account block. Identity, role and the account actions live
                      WITH the user in the switcher panel (UPM-150), not
                      floating loose in the brand menu. */}
                  <div className="nr-account">
                    <span className="nr-account-avatar">{initialsOf(displayName)}</span>
                    <span className="nr-account-text">
                      <span className="nr-account-name">{displayName || "Account"}</span>
                      <span className="nr-account-role">{roleLabel}</span>
                    </span>
                  </div>
                  <button type="button" className="nr-menu-row" role="menuitem" onClick={() => go("settings")}>
                    <span className="nr-menu-row-icon"><Settings size={15} strokeWidth={1.75} /></span>
                    Settings
                  </button>
                  <button type="button" className="nr-menu-row" role="menuitem" onClick={() => { closeMenus(); onLogout(); }}>
                    <span className="nr-menu-row-icon"><LogOut size={15} strokeWidth={1.75} /></span>
                    Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Empty: the switcher is replaced by a prompt card. */}
        {!collapsed && empty && (
          <div className="nr-switch-wrap">
            <div className="nr-empty-card">
              <div className="nr-empty-title">No analysis yet</div>
              <div className="nr-empty-body">AI already has an opinion. Start shaping it here.</div>
              <button type="button" className="nr-empty-cta" onClick={() => go("brand-search")}>Start analysis</button>
            </div>
          </div>
        )}

        {/* Collapsed: the avatar IS the trigger. */}
        {collapsed && !empty && (
          <div className="nr-switch-wrap" style={{ display: "flex", justifyContent: "center", padding: "0 0 10px" }}>
            <button
              type="button"
              className="nr-avatar"
              aria-label="Switch analysis"
              style={{ width: 32, height: 32, cursor: "pointer", border: "none" }}
              onClick={() => { setSwitcherOpen((v) => !v); setManageOpen(false); }}
            >
              {initialOf(activeBrandName)}
            </button>
            {switcherOpen && (
              <>
                <div className="nr-backdrop" onClick={closeMenus} />
                <div className="nr-menu nr-menu--flyout" style={{ top: 64 }} role="menu">
                  <button type="button" className="nr-menu-row" role="menuitem" onClick={() => { closeMenus(); onOpenBrandSwitcher?.(); }}>
                    <span className="nr-menu-row-icon"><Check size={15} strokeWidth={1.75} /></span>
                    {activeBrandName || "No analysis"}
                  </button>
                  <button type="button" className="nr-menu-row" role="menuitem" onClick={() => go("brand-search")}>
                    <span className="nr-menu-row-icon"><Plus size={15} strokeWidth={1.75} /></span>
                    New analysis
                  </button>
                  <div className="nr-menu-sep" />
                  {/* Account block. Identity, role and the account actions live
                      WITH the user in the switcher panel (UPM-150), not
                      floating loose in the brand menu. */}
                  <div className="nr-account">
                    <span className="nr-account-avatar">{initialsOf(displayName)}</span>
                    <span className="nr-account-text">
                      <span className="nr-account-name">{displayName || "Account"}</span>
                      <span className="nr-account-role">{roleLabel}</span>
                    </span>
                  </div>
                  <button type="button" className="nr-menu-row" role="menuitem" onClick={() => go("settings")}>
                    <span className="nr-menu-row-icon"><Settings size={15} strokeWidth={1.75} /></span>
                    Settings
                  </button>
                  <button type="button" className="nr-menu-row" role="menuitem" onClick={() => { closeMenus(); onLogout(); }}>
                    <span className="nr-menu-row-icon"><LogOut size={15} strokeWidth={1.75} /></span>
                    Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* 3. Search. The "/" opens the existing palette; there is no "+". */}
        {!collapsed ? (
          <div className="nr-search-wrap">
            <button type="button" className="nr-search" onClick={() => onOpenCommandPalette?.()}>
              <Search size={14} strokeWidth={1.75} />
              <span className="nr-search-label">Search or jump to</span>
              <kbd className="nr-kbd">/</kbd>
            </button>
          </div>
        ) : (
          <div className="nr-search-wrap" style={{ display: "flex", justifyContent: "center", padding: "0 0 12px" }}>
            <button
              type="button"
              className="nr-chip"
              aria-label="Search"
              style={{ width: 34, height: 34 }}
              onClick={() => onOpenCommandPalette?.()}
            >
              <Search size={16} strokeWidth={1.75} />
            </button>
          </div>
        )}

        {/* 4. Module list — the only scrolling region. */}
        <nav className={`nr-nav${empty ? " nr-nav--inert" : ""}`}>
          {collapsed ? (
            <div className="nr-collapsed-stack">
              {coreGroups.map((g, gi) => (
                <div key={g.label} className="nr-collapsed-stack">
                  {gi > 0 && <span className="nr-chip-split" />}
                  {g.items.map(renderChip)}
                </div>
              ))}
            </div>
          ) : (
            coreGroups.map((g) => (
              <div key={g.label} className="nr-group">
                <div className="nr-group-label">{g.label}</div>
                {g.items.map((f) => renderRow(f))}
              </div>
            ))
          )}
        </nav>

        {/* 5. The plinth: a different material, not just different spacing. */}
        <div className="nr-plinth">
          {/* Advanced grows UPWARD from behind its pinned header. */}
          {!collapsed && (
            <div className={`nr-adv-panel${advancedOpen ? " nr-adv-panel--open" : ""}`}>
              <div className="nr-adv-rows">
                {visibleAdvanced.map((f, i) => (
                  <div key={f.id} className="nr-adv-row-wrap" style={{ display: "contents" }}>
                    <div style={{ animationDelay: `${i * 22}ms`, display: "contents" }}>
                      {renderRow(f, { advanced: true })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!collapsed ? (
            <button
              type="button"
              className="nr-adv-header"
              aria-expanded={advancedOpen}
              onClick={() => setAdvancedOpen((v) => !v)}
            >
              <span className="nr-adv-header-label">Advanced</span>
              {!advancedOpen && lockedCount > 0 && (
                <span className="nr-lock-count">
                  <Lock size={12} strokeWidth={1.75} />
                  {lockedCount}
                </span>
              )}
              <span className={`nr-chev${advancedOpen ? " nr-chev--open" : ""}`}>
                <ChevronUp size={14} strokeWidth={1.75} />
              </span>
            </button>
          ) : (
            <div style={{ display: "flex", justifyContent: "center", padding: "8px 0" }}>
              <button
                type="button"
                className="nr-adv-chip"
                aria-label="Advanced"
                aria-expanded={advancedOpen}
                onClick={() => { onCollapsedChange(false); setAdvancedOpen(true); }}
              >
                <ChevronUp size={14} strokeWidth={1.75} />
              </button>
            </div>
          )}

          {/* 6. Utility bar */}
          <div className="nr-util" style={collapsed ? { justifyContent: "center", flexWrap: "wrap" } : undefined}>
            <button
              type="button"
              className={`${collapsed ? "nr-iconbtn" : "nr-manage"}${manageOpen ? " nr-manage--open" : ""}`}
              aria-expanded={manageOpen}
              aria-label={collapsed ? "Manage" : undefined}
              onClick={() => { setManageOpen((v) => !v); setSwitcherOpen(false); }}
            >
              <Settings size={15} strokeWidth={1.75} />
              {!collapsed && <>Manage<ChevronRight size={13} strokeWidth={1.75} /></>}
            </button>
            {!collapsed && <span className="nr-util-spacer" />}
            <button
              type="button"
              className="nr-iconbtn"
              aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
              onClick={() => onCollapsedChange(!collapsed)}
            >
              {collapsed
                ? <ChevronsRight size={14} strokeWidth={1.75} />
                : <ChevronsLeft size={14} strokeWidth={1.75} />}
            </button>
            <button
              type="button"
              className={`nr-iconbtn${theme === "dark" ? " nr-iconbtn--teal" : ""}`}
              aria-label="Toggle theme"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            >
              {theme === "dark" ? <Sun size={14} strokeWidth={1.75} /> : <Moon size={14} strokeWidth={1.75} />}
            </button>
          </div>

          {manageOpen && (
            <>
              <div className="nr-backdrop" onClick={closeMenus} />
              <div className={`nr-menu ${collapsed ? "nr-menu--flyout-manage" : "nr-menu--manage"}`} role="menu">
                {manageItems.map((m) => {
                  const MIcon = m.icon;
                  return (
                    <button key={m.page} type="button" className="nr-menu-row" role="menuitem" onClick={() => go(m.page)}>
                      <span className="nr-menu-row-icon"><MIcon size={15} strokeWidth={1.75} /></span>
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {tip && collapsed && (
        <div className="nr-tip" style={{ left: 62 + 46, top: tip.top }}>{tip.text}</div>
      )}

      <LockedFeatureModal feature={lockedModal} onClose={() => setLockedModal(null)} />
    </>
  );
}

/** Re-exported so the shell can type its own state without reaching inside. */
export type { FeatureStates };
